import { Amplify } from "aws-amplify";
import { fetchAuthSession } from "aws-amplify/auth";
import outputs from "amplify_outputs.json";
import type { Schema } from "schema/data/resource";
import { generateClient } from "aws-amplify/data";

Amplify.configure(outputs);

export class UsersManagementService {
  private static client = generateClient<Schema>({
    authMode: "userPool",
  });

  private static async getAuthInfo() {
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken;
    const token = idToken?.toString();
    const userRole = idToken?.payload["custom:role"] as string;
    const userType = idToken?.payload["custom:userType"] as string;
    const userDomain = idToken?.payload["custom:domain"] as string;

    return { idToken, token, userRole, userType, userDomain };
  }

  static async getAllUsers(
    nextToken?: string,
    domain?: string,
    status?: string,
  ) {
    const { token } = await this.getAuthInfo();

    try {
      const response = await this.client.models.AccessRoles.list({
        authMode: "userPool",
        authToken: token,
        limit: 100,
        ...(nextToken && { nextToken: nextToken }),
        ...(domain && { domain: domain.toLowerCase() }),
        ...(status && { filter: { status: { eq: status } } }),
      });

      return response;
    } catch (error) {
      console.error("Error fetching users:", error);
      throw error;
    }
  }

  static async createUser(
    email: string,
    role: string,
    domain?: string,
    userType?: string,
  ) {
    const { token } = await this.getAuthInfo();

    try {
      const response = await this.client.mutations.CustomCreateAccessRoles(
        {
          email: email.toLowerCase(),
          role,
          ...(domain && { domain: domain.toLowerCase() }),
          ...(userType && { userType }),
        },
        {
          authMode: "userPool",
          authToken: token,
        },
      );
      return response;
    } catch (error) {
      console.error("Error creating user:", error);
      throw error;
    }
  }

  static async updateUser(
    domain: string,
    email: string,
    role: string,
    status: "PENDING" | "CONFIRMED" | "DISABLED",
  ) {
    const { token } = await this.getAuthInfo();

    try {
      const response = await this.client.models.AccessRoles.update(
        {
          domain: domain.toLowerCase(),
          email: email.toLowerCase(),
          role: role,
          status: status,
        },
        {
          authMode: "userPool",
          authToken: token,
        },
      );
      console.log("Update response:", response);
      return response;
    } catch (error) {
      console.error("Error updating user type:", error);
      throw error;
    }
  }

  static async deleteUser(domain: string, email: string) {
    const { token } = await this.getAuthInfo();

    try {
      const response = await this.client.models.AccessRoles.delete(
        {
          domain: domain.toLowerCase(),
          email: email.toLowerCase(),
        },
        {
          authMode: "userPool",
          authToken: token,
        },
      );
      return response;
    } catch (error) {
      console.error("Error deleting user:", error);
      throw error;
    }
  }

  static async getUserDomain(): Promise<string | null> {
    try {
      const { userDomain } = await this.getAuthInfo();

      return userDomain || null;
    } catch (error) {
      console.error("Error fetching user domain:", error);
      return null;
    }
  }

  static async hasAdminAccess(): Promise<boolean> {
    try {
      const { userRole } = await this.getAuthInfo();

      return userRole === "SuperAdmin" || userRole === "Support";
    } catch (error) {
      console.error("Error checking admin access:", error);
      return false;
    }
  }

  static async canAddAgents(): Promise<boolean> {
    try {
      const { userRole } = await this.getAuthInfo();

      return userRole === "SuperAdmin" || userRole === "Support";
    } catch (error) {
      console.error("Error checking add agent permissions:", error);
      return false;
    }
  }

  static async canManageAgents(): Promise<boolean> {
    try {
      const { userType, userRole } = await this.getAuthInfo();

      if (userRole === "SuperAdmin" || userRole === "Support") {
        return true;
      }
      if (
        userType === "PROVIDER" &&
        (userRole === "Owner" || userRole === "Admin")
      ) {
        return true;
      }

      return false;
    } catch (error) {
      console.error("Error checking agent management permissions:", error);
      return false;
    }
  }

  static async canAddInstitutions(): Promise<boolean> {
    try {
      const { userRole } = await this.getAuthInfo();

      return userRole === "SuperAdmin" || userRole === "Support";
    } catch (error) {
      console.error("Error checking add institution permissions:", error);
      return false;
    }
  }

  static async isOwnerOrAdmin(): Promise<boolean> {
    try {
      const { userType, userRole } = await this.getAuthInfo();

      if (
        (userType === "PROVIDER" || userType === "PARTNER") &&
        (userRole === "Owner" || userRole === "Admin")
      ) {
        return true;
      }

      return false;
    } catch (error) {
      console.error("Error checking institution admin status:", error);
      return false;
    }
  }

  static async isMember(): Promise<boolean> {
    try {
      const { userType, userRole } = await this.getAuthInfo();

      if (
        (userType === "PROVIDER" || userType === "PARTNER") &&
        userRole === "Member"
      ) {
        return true;
      }

      return false;
    } catch (error) {
      console.error("Error checking institution member status:", error);
      return false;
    }
  }
}
